import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import SportTypeSelector from './SportTypeSelector';

export default function BetaPopup({ onClose }) {
    const { t, i18n } = useTranslation('beta');
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        sport: '',
        region: '',
        schoolName: '',
        message: '',
    });
    const [sportTypeId, setSportTypeId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            setError(t('formRequired'));
            return;
        }
        setLoading(true);
        setError('');
        try {
            await axios.post(`${getApiBaseUrl()}/support/beta-application`, {
                ...form,
                sportTypeId,
            });
            setSuccess(true);
        } catch {
            setError(t('formError'));
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        border: '1px solid #E0E0E0',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 14,
        boxSizing: 'border-box',
    };

    const labelStyle = {
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        color: '#555',
        marginBottom: 4,
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
        }}>
            <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 32,
                maxWidth: 520,
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
            }}>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('close')}
                    style={{
                        position: 'absolute', right: 16, top: 16,
                        background: 'none', border: 'none',
                        fontSize: 24, cursor: 'pointer', color: '#888',
                    }}
                >
                    ×
                </button>

                {success ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
                        <h2 style={{ color: '#EB3333', marginBottom: 8 }}>
                            {t('successTitle')}
                        </h2>
                        <p style={{ color: '#666' }}>{t('successText')}</p>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                marginTop: 20,
                                background: '#EB3333',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 8,
                                padding: '10px 24px',
                                cursor: 'pointer',
                                fontWeight: 700,
                            }}
                        >
                            {t('close')}
                        </button>
                    </div>
                ) : (
                    <>
                        <h2 style={{
                            color: '#EB3333',
                            marginBottom: 4,
                            fontSize: 22,
                        }}>
                            🏆 {t('popupTitle')}
                        </h2>
                        <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
                            {t('popupSubtitle')}
                        </p>

                        <div style={{
                            background: '#FFF5F5',
                            borderRadius: 10,
                            padding: '12px 16px',
                            marginBottom: 20,
                            fontSize: 13,
                            color: '#c0392b',
                            borderLeft: '3px solid #EB3333',
                        }}>
                            🎁 {t('founderBadge')}
                        </div>

                        {error && (
                            <p style={{ color: '#EB3333', marginBottom: 12, fontSize: 13 }}>
                                {error}
                            </p>
                        )}

                        <form onSubmit={handleSubmit}>
                            {[
                                { key: 'name', label: t('fieldName'), required: true },
                                { key: 'email', label: t('fieldEmail'), required: true, type: 'email' },
                                { key: 'phone', label: t('fieldPhone') },
                                { key: 'schoolName', label: t('fieldSchool') },
                                { key: 'region', label: t('fieldRegion') },
                            ].map((field) => (
                                <div key={field.key} style={{ marginBottom: 12 }}>
                                    <label style={labelStyle}>
                                        {field.label}
                                        {field.required && (
                                            <span style={{ color: '#EB3333' }}> *</span>
                                        )}
                                    </label>
                                    <input
                                        type={field.type || 'text'}
                                        value={form[field.key]}
                                        onChange={(e) => setForm({
                                            ...form, [field.key]: e.target.value,
                                        })}
                                        style={inputStyle}
                                    />
                                </div>
                            ))}

                            <div style={{ marginBottom: 12 }}>
                                <label style={labelStyle}>{t('fieldSport')}</label>
                                <SportTypeSelector
                                    key={i18n.resolvedLanguage || i18n.language}
                                    value={sportTypeId}
                                    compact
                                    showSoon={false}
                                    onChange={(id, sport) => {
                                        const name = typeof sport === 'object' ? sport?.name : '';
                                        setSportTypeId(id);
                                        setForm((prev) => ({
                                            ...prev,
                                            sport: name || prev.sport,
                                        }));
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>
                                    {t('fieldMessage')}
                                </label>
                                <textarea
                                    value={form.message}
                                    onChange={(e) => setForm({
                                        ...form, message: e.target.value,
                                    })}
                                    rows={3}
                                    style={{
                                        ...inputStyle,
                                        resize: 'vertical',
                                    }}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    background: loading ? '#E0E0E0' : '#EB3333',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 10,
                                    padding: '14px',
                                    fontSize: 16,
                                    fontWeight: 700,
                                    cursor: loading ? 'default' : 'pointer',
                                }}
                            >
                                {loading ? t('sending') : t('submit')}
                            </button>

                            <p style={{
                                textAlign: 'center',
                                fontSize: 11,
                                color: '#AAA',
                                marginTop: 10,
                            }}>
                                {t('privacyNote')}
                            </p>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
